import { IChatRepository } from '../../domain/repositories/IChatRepository';
import { Chat } from '../../domain/entities/Chat';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { InternalServerError } from '../../application/errors/AppError';
import { Coach } from '../../types/index';

/**
 * API-based Chat Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class ChatRepository implements IChatRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();

 private parseAIResponse(rawText: string): {
    status: number;
    msg: string;
    original_response: string;
    data?: any;
  } {
    try {
      // Try to find any JSON block inside the AI's message
      const match = rawText.match(/\{[\s\S]*\}/);
      if (!match) {
        return {
          status: 420,
          msg: 'Plain text response',
          original_response: rawText,
        };
      }

      const jsonString = match[0];
      const parsed = JSON.parse(jsonString);

      return {
        status: parsed.status ?? 1,
        msg: parsed.msg ?? 'Success',
        original_response: parsed.original_response ?? rawText,
        data: parsed.data ?? {},
      };
    } catch (err) {
      return {
        status: 420,
        msg: 'Parse error',
        original_response: rawText,
      };
    }
  }

async fetchChatsWithAI(
  userId: string,
  pageNumber = 1,
  pageSize = 10,
  coachId?: string | null
): Promise<Chat[]> {
  try {
    // Build URL with correct param order: UserId -> CoachId -> PageNumber -> PageSize
  let url = `${this.config.aiBaseUrl}/Chats?UserId=${userId}`;
    if (coachId) {
      url += `&CoachId=${coachId}`;
    }
    url += `&PageNumber=${pageNumber}&PageSize=${pageSize}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "X-Api-Key": this.config.apiKey,
        accept: "application/json",
      },
    });

    if (!response.ok) {
      const text = await response.text();
      this.logger.error("Failed to fetch chats", new Error(text), { userId, coachId });
      throw new InternalServerError(`Failed to fetch chats: ${text}`);
    }

    const data = await response.json();
    const chatItems = data?.result?.items || [];

    return chatItems.map((item: any) => {
      let messageContent = item.content;
      let parsedAI: any = null;

      if (item.role === "assistant" && typeof messageContent === "string") {
        const parsed = this.parseAIResponse(messageContent);

        // if (parsed.msg) { 
        //     messageContent = parsed.msg;
        //   } else if (parsed.original_response) {
        //     messageContent = parsed.original_response;
        //   } else {
        //     messageContent = messageContent;
        //   }

        if (parsed.original_response && parsed.original_response.trim() !== "") {
            messageContent = parsed.original_response;
        } else if (parsed.msg && parsed.msg.trim() !== "") {
          messageContent = parsed.msg;
        }

        parsedAI = parsed;
      }


      return Chat.fromData({
        id: item.id,
        user_id: item.userId,
        message: messageContent,
        type: item.role === "assistant" ? "ai" : "user",
        timestamp: item.createdAt,
        is_read: false,
        metadata: parsedAI,
          filePath: item.filePath, 
      });
    });
  } catch (error) {
    this.logger.error("Error fetching chats", error as Error, { userId, coachId });
    throw error;
  }
}

async sendMessageToAI(
  question: string,
  userId: string,
  coachId?: string | null,
  isCoachRequest: boolean = true,
  imageFile?: File
): Promise<{ aiContent: string; parsedAI?: unknown }> {
  try {
    const formData = new FormData();

    // Append fields in the same order as your curl
    formData.append("Question", question || "");
    formData.append("UserId", userId || "");
    formData.append("CoachId", coachId || "");
    formData.append("Temperature", "");
    formData.append("MaxTokens", "");
    formData.append("IsCoachRequest", String(isCoachRequest));
    formData.append("FilePath", "");
    formData.append("IsStrickSwimmer" , String(false));

    //   formData.append("IsStrickSwimmer" , String(false)

    if (imageFile) {
      formData.append("Image", imageFile, imageFile.name);
    }

   const response = await fetch(`${this.config.aiBaseUrl}/Chats/ask`, {
      method: "POST",
      headers: {
      "X-Api-Key": this.config.apiKey,
        Accept: "application/json",
      },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      this.logger.error(`AI request failed: ${response.status} - ${text}`);
      throw new InternalServerError(`AI API failed: ${response.status}`);
    }

    const data = await response.json();
    const aiContent: string =
    data?.result?.assistantMessage?.content ?? "No AI response received.";

const parsedAI =
  typeof aiContent === "string"
    ? this.parseAIResponse(aiContent)
    : null;

    console.log('parsed ai' , parsedAI);

return { aiContent, parsedAI };

  } catch (error) {
    this.logger.error("Error sending message to AI:", error as Error);
    throw new InternalServerError((error as Error).message || "Failed to send message to AI");
  }
}

async deleteAIAllMessages(userId: string, coachId?: string | null): Promise<void> {
  try {
  let url = `${this.config.aiBaseUrl}/Chats/delete-all-message?UserId=${userId}`;
    if (coachId) {
      url += `&CoachId=${coachId}`;
    }

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "X-Api-Key": this.config.apiKey,
        accept: "*/*",
      },
    });

    if (!response.ok) {
      const text = await response.text();
      this.logger.error(`Failed to delete all messages: ${text}`, new Error(text), { userId, coachId });
      throw new InternalServerError(`Failed to delete all messages: ${text}`);
    }

    this.logger.info(`All chat messages deleted successfully for userId=${userId}, coachId=${coachId}`);
  } catch (error) {
    this.logger.error("Error deleting all messages", error as Error, { userId, coachId });
    throw error;
  }
}

 async fetchCoaches(swimmerId: string): Promise<Coach[]> {
    try {

      const formData = new URLSearchParams();
      formData.append("swimmer_id", swimmerId);
      formData.append("status", "1");

      const response = await fetch(
        `${this.config.baseUrl}/coaches/get_swimmer_coaches`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: formData,
        }
      );

      if (!response.ok)
        throw new Error(`Failed to fetch coaches: ${response.statusText}`);

      const data = await response.json();
      const parsedData = JSON.parse(data.data);

      return parsedData.map((coach: any) => ({
        id: coach.id,
        name: `${coach.fname} ${coach.lname}`,
        email: coach.email,
        avatar: coach.image
          ? coach.image
          : `${coach.fname[0]}${coach.lname[0]}`.toUpperCase(),
        lang: coach.lang,
        status: "active",
      }));
    } catch (error) {
      throw new InternalServerError("Failed to fetch coaches");
    }
  }




  async findById(id: string): Promise<Chat | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/${id}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new InternalServerError('Failed to fetch chat');
      }

      const data = await response.json();
      return Chat.fromData(data);
    } catch (error) {
      this.logger.error('Failed to find chat by ID', error as Error, { chatId: id });
      throw error;
    }
  }

  async findByUserId(userId: string): Promise<Chat[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch user chats');
      }

      // const data = await response.json();
      // return data.chats?.map((chat: unknown) => Chat.fromData(chat as Record<string, unknown>)) || [];
      const data = await response.json();
      return data.chats?.map((chat: any) => Chat.fromData(chat)) || [];
    } catch (error) {
      this.logger.error('Failed to find chats by user ID', error as Error, { userId });
      throw error;
    }
  }

  async findByConversation(conversationId: string): Promise<Chat[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/conversation/${conversationId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch conversation chats');
      }

      //const data = await response.json();
    //  return data.chats?.map((chat: unknown) => Chat.fromData(chat as Record<string, unknown>)) || [];
      const data = await response.json();
      return data.chats?.map((chat: any) => Chat.fromData(chat)) || [];
    } catch (error) {
      this.logger.error('Failed to find chats by conversation', error as Error, { conversationId });
      throw error;
    }
  }

  async save(chat: Chat): Promise<Chat> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(chat.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to save chat');
      }

      const data = await response.json();
      return Chat.fromData(data);
    } catch (error) {
      this.logger.error('Failed to save chat', error as Error, { chatId: chat.id });
      throw error;
    }
  }

  async update(chat: Chat): Promise<Chat> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/${chat.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(chat.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to update chat');
      }

      const data = await response.json();
      return Chat.fromData(data);
    } catch (error) {
      this.logger.error('Failed to update chat', error as Error, { chatId: chat.id });
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete chat');
      }
    } catch (error) {
      this.logger.error('Failed to delete chat', error as Error, { chatId: id });
      throw error;
    }
  }

  async markAsRead(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/${id}/read`, {
        method: 'PUT',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to mark chat as read');
      }
    } catch (error) {
      this.logger.error('Failed to mark chat as read', error as Error, { chatId: id });
      throw error;
    }
  }

  async markAllAsRead(userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to mark all chats as read');
      }
    } catch (error) {
      this.logger.error('Failed to mark all chats as read', error as Error, { userId });
      throw error;
    }
  }

  async getUnreadCount(userId: string): Promise<number> {
    try {
      const response = await fetch(`${this.config.baseUrl}/chats/unread-count?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch unread count');
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      this.logger.error('Failed to get unread count', error as Error, { userId });
      throw error;
    }
  }

  async search(userId: string, query: string): Promise<Chat[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        search: query
      });

      const response = await fetch(`${this.config.baseUrl}/chats/search?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to search chats');
      }

      // const data = await response.json();
      // return data.chats?.map((chat: unknown) => Chat.fromData(chat as Record<string, unknown>)) || [];
      const data = await response.json();
      return data.chats?.map((chat: any) => Chat.fromData(chat)) || [];
    } catch (error) {
      this.logger.error('Failed to search chats', error as Error, { userId, query });
      throw error;
    }
  }

  async findByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Chat[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      const response = await fetch(`${this.config.baseUrl}/chats/date-range?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch chats by date range');
      }

      // const data = await response.json();
      // return data.chats?.map((chat: unknown) => Chat.fromData(chat as Record<string, unknown>)) || [];
      const data = await response.json();
      return data.chats?.map((chat: any) => Chat.fromData(chat)) || [];
    } catch (error) {
      this.logger.error('Failed to find chats by date range', error as Error, { 
        userId, 
        startDate, 
        endDate 
      });
      throw error;
    }
  }

  async findPaginated(userId: string, page: number, limit: number): Promise<{
    chats: Chat[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await fetch(`${this.config.baseUrl}/chats?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch paginated chats');
      }

      const data = await response.json();
      return {
        chats: data.chats,
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
        totalPages: data.total_pages || 0
      };
    } catch (error) {
      this.logger.error('Failed to get paginated chats', error as Error, { 
        userId, 
        page, 
        limit 
      });
      throw error;
    }
  }
}
